<script setup lang="ts">
import { ref, watch } from 'vue';

import { useI18n } from 'vue-i18n';
import {
  LpInvisibleInput,
} from '@thehotelsnetwork/lampo';

import Loader from '@/components/Loader/Loader.vue';
import checkCompsetName from '@/utils/checkCompsetName';

const props = defineProps<{
  modelValue: string,
  placeholder: string,
  editModeActive?: boolean,
}>();

const emit = defineEmits(['update:modelValue', 'editOn', 'editOff', 'loading', 'error']);

const { t } = useI18n();
const lastCorrectValue = ref(props.modelValue);
const error = ref(false);
const isLoadingValidation = ref(false);
const errorMessage = ref('');
const isEditing = ref(props.editModeActive);

const setIsEditing = (val: boolean) => {
  isEditing.value = val;
  emit('editOn');
};

const setIsLoading = (val: boolean) => {
  isLoadingValidation.value = val;
};

const checkValue = async (v: string | number) => {
  const value = String(v);

  errorMessage.value = '';

  if (!value) {
    errorMessage.value = 'Compset name cannot be empty';
    return;
  }

  if (value.length > 25) {
    errorMessage.value = 'Compset name cannot have more than 25 characters';
    return;
  }

  if (value !== lastCorrectValue.value) {
    setIsLoading(true);
    emit('loading');

    try {
      const isValid = await checkCompsetName(value);

      if (!isValid) {
        errorMessage.value = 'A Compset with this name already exists';
        setIsLoading(false);
        return;
      }
    } catch {
      errorMessage.value = 'An error occurred, please try again later';
      setIsLoading(false);
      return;
    }
  }

  setIsLoading(false);
  lastCorrectValue.value = value;

  emit('update:modelValue', value);
  setIsEditing(false);
  emit('editOff');
};

const onDiscard = () => {
  checkValue(lastCorrectValue.value);
};

watch(errorMessage, () => {
  error.value = !!errorMessage.value;

  if (error.value) {
    emit('error');
  }
});
</script>

<template>
  <div
    :class="[
      'whitespace-nowrap',
      { 'flex items-center gap-4 pointer-events-none': isLoadingValidation },
    ]"
  >
    <LpInvisibleInput
      v-model="$props.modelValue"
      v-model:error="error"
      size="big"
      :placeholder="placeholder"
      :edit-mode-active="!isLoadingValidation && editModeActive"
      @update:model-value="checkValue"
      @discard="onDiscard"
      @edit-on="setIsEditing(true)"
    />
    <Loader v-if="isLoadingValidation" />
    <div
      v-else-if="isEditing"
      class="text-sm"
    >
      <span
        v-if="errorMessage"
        class="text-danger-500"
      >
        {{ t(errorMessage) }}
      </span>

      <span
        v-else
        class="text-base-700"
      >
        {{ t('Have in mind different Compsets can\'t have the same name') }}
      </span>
    </div>
  </div>
</template>
